import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, ChevronDown } from 'lucide-react';
import { ColDef, ValueFormatterParams, SelectionChangedEvent } from 'ag-grid-community';
import { parseISO, format } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';
import { useAccessControl } from '@/contexts/AccessControlContext';
import { usePagination } from '@/hooks/usePagination';
import { groupService, profileService, type GroupRow } from '@/services';
import { AGDataGrid } from '@/components/ui/ag-data-grid';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import NewGroupDialog from '@/components/security/NewGroupDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return '\u2014';
  try {
    return format(parseISO(dateStr), 'MMM d, yyyy h:mm a');
  } catch {
    return '\u2014';
  }
}

export default function SecurityGroups() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { invalidateCache: invalidateAccessCache } = useAccessControl();

  const {
    pageIndex,
    pageSize,
    searchValue,
    debouncedSearch,
    setSearchValue,
    setPageIndex,
    setPageSize,
    pageSizeOptions,
  } = usePagination({ defaultPageSize: 25, storageKey: 'security-groups' });

  const [companyId, setCompanyId] = useState<string | null>(null);
  const [allGroups, setAllGroups] = useState<GroupRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRows, setSelectedRows] = useState<GroupRow[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [newDialogOpen, setNewDialogOpen] = useState(false);

  // Fetch company ID on mount
  useEffect(() => {
    const fetchCompanyId = async () => {
      if (!user) return;
      const { data: profile } = await profileService.get(user.id);
      if (profile?.company_id) {
        setCompanyId(profile.company_id);
      } else {
        setIsLoading(false);
      }
    };
    fetchCompanyId();
  }, [user]);

  // Fetch groups
  const fetchGroups = useCallback(async () => {
    if (!companyId) return;
    setIsLoading(true);
    const result = await groupService.list(companyId);
    if (result.error) {
      toast.error('Failed to load groups');
    } else {
      setAllGroups(result.data);
    }
    setIsLoading(false);
  }, [companyId]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  // Client-side search filtering
  const filteredGroups = useMemo(() => {
    if (!debouncedSearch) return allGroups;
    const search = debouncedSearch.toLowerCase();
    return allGroups.filter((g) => g.name?.toLowerCase().includes(search));
  }, [allGroups, debouncedSearch]);

  // Client-side pagination
  const paginatedGroups = useMemo(() => {
    const start = pageIndex * pageSize;
    return filteredGroups.slice(start, start + pageSize);
  }, [filteredGroups, pageIndex, pageSize]);

  const totalCount = filteredGroups.length;

  // Selection handler
  const handleSelectionChanged = useCallback(
    (event: SelectionChangedEvent<GroupRow>) => {
      setSelectedRows(event.api.getSelectedRows());
    },
    []
  );

  // Delete handler
  const confirmDelete = async () => {
    if (selectedRows.length === 0 || !companyId || !user?.id) return;

    try {
      for (const group of selectedRows) {
        const result = await groupService.delete(
          group.id,
          group as unknown as Record<string, unknown>,
          { companyId, userId: user.id }
        );
        if (result.error) throw result.error;
      }

      toast.success(
        selectedRows.length === 1
          ? 'Group deleted successfully'
          : `${selectedRows.length} groups deleted successfully`
      );
      setSelectedRows([]);
      invalidateAccessCache();
      await fetchGroups();
    } catch (error: any) {
      console.error('Error deleting group(s):', error);
      toast.error(error.message || 'Failed to delete group(s)');
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  // Column definitions
  const columnDefs = useMemo<ColDef<GroupRow>[]>(
    () => [
      {
        headerName: 'Name',
        field: 'name',
        flex: 1,
        minWidth: 200,
      },
      {
        headerName: 'Active',
        field: 'is_active',
        width: 100,
        cellRenderer: (params: { data: GroupRow | undefined }) => {
          if (!params.data) return null;
          return (
            <span className={params.data.is_active ? 'text-green-600' : 'text-muted-foreground'}>
              {params.data.is_active ? 'Yes' : 'No'}
            </span>
          );
        },
      },
      {
        headerName: 'Created',
        field: 'created',
        width: 170,
        valueFormatter: (params: ValueFormatterParams<GroupRow>) =>
          formatDateTime(params.value),
      },
      {
        headerName: 'Modified',
        field: 'modified',
        width: 170,
        valueFormatter: (params: ValueFormatterParams<GroupRow>) =>
          formatDateTime(params.value),
      },
    ],
    []
  );

  return (
    <div className="animate-slide-up space-y-4">
      <div>
        <h1 className="page-title">Groups</h1>
        <p className="text-sm text-muted-foreground">Manage user groups and team access.</p>
      </div>

      <AGDataGrid<GroupRow>
        rowData={paginatedGroups}
        columnDefs={columnDefs}
        totalCount={totalCount}
        pageIndex={pageIndex}
        pageSize={pageSize}
        pageSizeOptions={pageSizeOptions}
        searchValue={searchValue}
        searchPlaceholder="Search groups..."
        isLoading={isLoading}
        onSearchChange={setSearchValue}
        onPageChange={setPageIndex}
        onPageSizeChange={setPageSize}
        onRowClicked={(row) => navigate(`/security/groups/${row.id}`)}
        onSelectionChanged={handleSelectionChanged}
        rowSelection={{
          mode: 'multiRow' as const,
          checkboxes: true,
          headerCheckbox: true,
        }}
        toolbarActions={
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={selectedRows.length === 0}>
                  Actions
                  <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => setDeleteDialogOpen(true)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete ({selectedRows.length})
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="outline" size="sm" onClick={() => setNewDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              New Group
            </Button>
          </div>
        }
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedRows.length === 1 ? 'Group' : 'Groups'}</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedRows.length === 1
                ? `Are you sure you want to delete the group "${selectedRows[0]?.name}"? This will remove all member assignments and permissions associated with this group. This action cannot be undone.`
                : `Are you sure you want to delete ${selectedRows.length} groups? This will remove all member assignments and permissions associated with these groups. This action cannot be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {companyId && user && (
        <NewGroupDialog
          open={newDialogOpen}
          onOpenChange={setNewDialogOpen}
          context={{ companyId, userId: user.id }}
          onCreated={fetchGroups}
        />
      )}
    </div>
  );
}
